import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Button } from '../Button';
import {
  useCreatePlan,
  useUpdatePlan,
  type PlanData,
} from '../../lib/mutations/plans';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  includedTokens: z.coerce
    .number()
    .int()
    .positive('Must be a positive integer'),
  overageRate: z.string().regex(/^\d+\.\d+$/, 'e.g. 0.0025'),
  platformAccessFee: z.string().regex(/^\d+\.\d+$/, 'e.g. 499.00'),
  maxUsers: z.coerce.number().int().positive('Must be a positive integer'),
});

type FormValues = z.infer<typeof schema>;

export default function NewPlanForm({
  onCloseModal,
  plan,
}: {
  onCloseModal?: () => void;
  plan?: PlanData;
}) {
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const isPending = createPlan.isPending || updatePlan.isPending;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: plan
      ? {
          name: plan.name,
          includedTokens: plan.includedTokens,
          overageRate: plan.overageRate,
          platformAccessFee: plan.platformAccessFee,
          maxUsers: plan.maxUsers,
        }
      : undefined,
  });

  function onSubmit(v: FormValues) {
    if (plan) {
      updatePlan.mutate({ id: plan.id, ...v }, { onSuccess: onCloseModal });
    } else {
      createPlan.mutate(v, { onSuccess: onCloseModal });
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/8 px-6 py-4">
        <h2 className="text-primary text-base font-semibold">
          {plan ? 'Edit plan tier' : 'New plan tier'}
        </h2>
        <button
          type="button"
          onClick={onCloseModal}
          className="text-muted hover:text-primary transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-6 py-5">
        <Field label="Plan name" error={errors.name?.message}>
          <input
            {...register('name')}
            placeholder="e.g. Professional"
            className={inputCls}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Token allowance" error={errors.includedTokens?.message}>
            <input
              {...register('includedTokens')}
              type="number"
              placeholder="250000"
              className={inputCls}
            />
          </Field>
          <Field label="Overage rate" error={errors.overageRate?.message}>
            <input
              {...register('overageRate')}
              placeholder="0.0025"
              className={inputCls}
            />
          </Field>
          <Field
            label="Base price ($/mo)"
            error={errors.platformAccessFee?.message}
          >
            <input
              {...register('platformAccessFee')}
              placeholder="499"
              className={inputCls}
            />
          </Field>
          <Field label="Max users" error={errors.maxUsers?.message}>
            <input
              {...register('maxUsers')}
              type="number"
              placeholder="10"
              className={inputCls}
            />
          </Field>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-white/8 pt-4">
          <Button type="button" variant="secondary" onClick={onCloseModal}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending
              ? plan
                ? 'Saving…'
                : 'Creating…'
              : plan
                ? 'Save changes'
                : 'Create plan'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded border border-white/10 bg-white/4 px-3 py-2 text-sm text-primary placeholder:text-muted focus:border-[--color-accent] focus:outline-none';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-danger mt-1 text-xs">{error}</p>}
    </div>
  );
}
